import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowRightLong, RightChevron } from "@/components/icons";
import type { PendingExam } from "../../types/client-dashboard.types";
import styles from "./PendingExamsList.module.scss";

function getProgressColor(percent: number): "orange" | "blue" | "grey" {
  if (percent <= 0) return "grey";
  if (percent >= 80) return "blue";
  return "orange";
}

export interface PendingExamsListProps {
  items: PendingExam[];
}

export const PendingExamsList: React.FC<PendingExamsListProps> = ({
  items,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>
          {t("clientDashboard.pendingExams.title")}
        </h3>
        <button
          type="button"
          className={styles.sectionLink}
          onClick={() => navigate("/dashboard/client-exams")}
          aria-label={t("clientDashboard.pendingExams.viewAll")}
        >
          <RightChevron width={20} height={20} />
        </button>
      </div>
      <ul className={styles.list}>
        {items.map((exam) => {
          const percent = exam.total
            ? Math.round((exam.score / exam.total) * 100)
            : 0;
          const barColor = getProgressColor(percent);
          return (
            <li key={exam.id} className={styles.item}>
              <div className={styles.itemTopRow}>
                <span className={styles.itemTitle}>{exam.title}</span>
                <span className={styles.countdownChip}>{exam.countdown}</span>
                <button
                  type="button"
                  className={styles.detailBtn}
                  onClick={() => navigate("/dashboard/client-exams")}
                  aria-label={t("clientDashboard.pendingExams.detail")}
                >
                  <ArrowRightLong width={18} height={18} />
                </button>
              </div>
              <div className={styles.progressTrack}>
                <div
                  className={`${styles.progressFill} ${styles[`progressFill_${barColor}`]}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className={styles.itemBottomRow}>
                <span className={styles.timeMeta}>
                  {exam.timeSpent}/{exam.timeTotal}
                </span>
                <span
                  className={`${styles.scoreMeta} ${styles[`scoreMeta_${barColor}`]}`}
                >
                  {exam.score}/{exam.total}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PendingExamsList;
